import React, { useState, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText 
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Upload as UploadIcon, 
  Download as DownloadIcon 
} from '@mui/icons-material';
import * as PptxGenJS from 'pptxgenjs';

export default function PowerPointApp() {
  const [slides, setSlides] = useState([
    { id: 1, elements: [{ type: 'text', content: 'Titre de la présentation', x: 1, y: 1, w: 8, h: 1 }] }
  ]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      // Pour l'instant, nous allons simplement ajouter une diapositive vide
      // Dans une version future, nous pourrions utiliser pptx.js pour parser le fichier
      addNewSlide();
      console.log('Fichier chargé (lecture non implémentée)');
    };
    reader.onerror = () => {
      console.error('Erreur lors de la lecture du fichier');
    };
    reader.readAsArrayBuffer(file);
  };

  const addNewSlide = () => {
    const newSlide = {
      id: Date.now(),
      elements: [
        { type: 'text', content: `Diapositive ${slides.length + 1}`, x: 1, y: 1, w: 8, h: 1 }
      ]
    };
    setSlides([...slides, newSlide]);
    setCurrentSlide(slides.length);
  };

  const deleteCurrentSlide = () => {
    if (slides.length <= 1) return;
    const newSlides = slides.filter((_, index) => index !== currentSlide);
    setSlides(newSlides);
    setCurrentSlide(Math.min(currentSlide, newSlides.length - 1));
  };

  const exportToPPTX = () => {
    try {
      const pptx = new PptxGenJS.default();
      
      // Créer une diapositive pour chaque diapositive dans l'état
      slides.forEach((slide, index) => {
        const slideObj = pptx.addSlide();
        slideObj.addText(
          `Diapositive ${index + 1}`, 
          { x: 0.5, y: 0.5, w: 9, h: 0.5, fontSize: 18, bold: true }
        );
        
        // Ajouter chaque élément de la diapositive
        slide.elements.forEach(element => {
          if (element.type === 'text') {
            slideObj.addText(element.content, { 
              x: element.x, 
              y: element.y, 
              w: element.w, 
              h: element.h,
              fontSize: 14
            });
          }
        });
      });
      
      // Télécharger le fichier
      pptx.writeFile({ fileName: 'presentation.pptx' });
    } catch (error) {
      console.error('Erreur lors de la génération du fichier PPTX:', error);
      alert('Une erreur est survenue lors de la génération du fichier');
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Barre latérale des diapositives */}
      <Drawer
        variant="permanent"
        sx={{
          width: 200,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 200,
            boxSizing: 'border-box',
            pt: 8,
            bgcolor: '#f5f5f5',
          },
        }}
      >
        <List>
          {slides.map((slide, index) => (
            <ListItem 
              button 
              key={slide.id}
              selected={index === currentSlide}
              onClick={() => setCurrentSlide(index)}
              sx={{
                mb: 1,
                bgcolor: index === currentSlide ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemText 
                primary={`Diapositive ${index + 1}`} 
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: index === currentSlide ? 'medium' : 'regular',
                }}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ p: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            fullWidth 
            variant="outlined" 
            startIcon={<AddIcon />} 
            onClick={addNewSlide}
            sx={{ mt: 1 }}
          >
            Ajouter
          </Button>
        </Box>
      </Drawer>

      {/* Zone d'édition principale */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Barre d'outils */}
        <Box sx={{ 
          p: 1, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          gap: 1,
          bgcolor: 'background.paper',
          flexShrink: 0,
          zIndex: 1,
        }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            size="small"
          >
            Ouvrir
            <input
              type="file"
              hidden
              accept=".pptx"
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToPPTX}
            size="small"
            color="primary"
          >
            Enregistrer
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={deleteCurrentSlide}
            disabled={slides.length <= 1}
            size="small"
            color="error"
            sx={{ ml: 'auto' }}
          >
            Supprimer
          </Button>
        </Box>

        {/* Zone de la diapositive */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 3,
          bgcolor: '#f0f0f0',
          overflow: 'auto',
        }}>
          {slides.length > 0 ? (
            <Paper
              elevation={3}
              sx={{
                width: '90%',
                maxWidth: '900px',
                height: '80%',
                aspectRatio: '16/9',
                position: 'relative',
                bgcolor: 'white',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {slides[currentSlide]?.elements.map((element, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    left: `${element.x * 10}%`,
                    top: `${element.y * 10}%`,
                    width: `${element.w * 10}%`,
                    minHeight: '24px',
                    border: '1px dashed #ccc',
                    p: 1,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                    cursor: 'move',
                    userSelect: 'none',
                  }}
                  contentEditable
                  suppressContentEditableWarning={true}
                  onBlur={(e) => {
                    const newSlides = [...slides];
                    const elementIndex = newSlides[currentSlide].elements.findIndex((_, i) => i === index);
                    if (elementIndex !== -1) {
                      newSlides[currentSlide].elements[elementIndex].content = e.target.innerText;
                      setSlides(newSlides);
                    }
                  }}
                >
                  {element.content}
                </Box>
              ))}
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Aucune diapositive
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: '400px' }}>
                Commencez par créer une nouvelle présentation ou ouvrez un fichier existant.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addNewSlide}
                sx={{ mt: 2 }}
              >
                Créer une présentation
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
